export const ijtaiHero = {
  badge: "Peer-Reviewed Publication",
  title: "International Journal of Textile & Apparel Innovation",
  subtitle:
    "The first peer-reviewed journal dedicated to textile and apparel innovation in South Asia. Published bi-annually, IJTAI features validated research, factory case studies, technology assessments, and policy commentary from Bangladesh and global contributors.",
  ctaPrimary: "Submit a Paper",
  ctaSecondary: "Read Latest Issue",
};

export const ijtaiWhatIs = {
  title: "What is IJTAI?",
  paragraphs: [
    "IJTAI - the International Journal of Textile & Apparel Innovation - is a bi-annual, peer-reviewed academic journal that serves as the primary knowledge repository for textile and apparel innovation research originating from Bangladesh and South Asia.",
    "The journal fills a critical gap. While global textile research is well-documented in Western journals, the specific challenges, innovations, and contextual solutions of Bangladesh's T&A sector often go unpublished. IJTAI provides a platform where factory-validated research, local technology assessments, and sector-specific policy analysis receive the rigorous peer review they deserve.",
    "Each issue includes original research articles, case studies from PIARC and PIIC deployments, technology reviews, and policy commentary. All submissions undergo double-blind peer review by a panel of academic and industry reviewers. Accepted papers are assigned DOIs and indexed in major research databases.",
  ],
  highlights: [
    { label: "Frequency", value: "Bi-annual" },
    { label: "Review Type", value: "Double-blind" },
    { label: "Issues Published", value: "12+" },
    { label: "Indexed", value: "Yes" },
  ],
};

export const ijtaiPublicationTypes = [
  {
    name: "Research Articles",
    icon: "ri-microscope-line",
    description:
      "Original, peer-reviewed research presenting new findings in textile innovation, process optimization, sustainability, or technology adoption.",
    wordCount: "4,000–6,000",
    reviewTime: "8–12 weeks",
  },
  {
    name: "Factory Case Studies",
    icon: "ri-building-2-line",
    description:
      "Documented innovation deployments from PIIC, PTIM, or PIARC programmes with quantitative before-and-after metrics and implementation details.",
    wordCount: "2,500–4,000",
    reviewTime: "6–8 weeks",
  },
  {
    name: "Technology Reviews",
    icon: "ri-cpu-line",
    description:
      "Critical assessments of emerging technologies, machinery, software, or materials relevant to Bangladesh's textile and apparel sector.",
    wordCount: "3,000–5,000",
    reviewTime: "6–10 weeks",
  },
  {
    name: "Policy Commentary",
    icon: "ri-government-line",
    description:
      "Analysis of regulatory, trade, or industrial policy developments and their implications for textile innovation and competitiveness.",
    wordCount: "2,000–3,500",
    reviewTime: "4–6 weeks",
  },
  {
    name: "Short Communications",
    icon: "ri-flashlight-line",
    description:
      "Concise reports of preliminary findings, novel observations, or methodological innovations that warrant rapid dissemination.",
    wordCount: "1,500–2,500",
    reviewTime: "4–6 weeks",
  },
  {
    name: "Book Reviews",
    icon: "ri-book-open-line",
    description:
      "Critical reviews of recent books, reports, or whitepapers relevant to textile innovation, sustainability, or manufacturing excellence.",
    wordCount: "1,000–2,000",
    reviewTime: "4 weeks",
  },
];

export const ijtaiProcessSteps = [
  {
    phase: "Step 1",
    week: "Submission",
    title: "Manuscript Submission",
    description:
      "Authors submit manuscripts through the IJTAI online portal with abstract, keywords, methodology, findings, and references formatted according to the journal's author guidelines.",
    activities: [
      "Online portal submission with required metadata",
      "Plagiarism screening and formatting check",
      "Editorial pre-screening for scope fit",
      "Acknowledgment and manuscript ID assignment",
    ],
  },
  {
    phase: "Step 2",
    week: "4–6 weeks",
    title: "Peer Review",
    description:
      "The editor assigns two independent reviewers with expertise in the manuscript's subject area. Double-blind review ensures objectivity. Reviewers assess originality, methodology, validity, and contribution.",
    activities: [
      "Double-blind reviewer assignment",
      "Technical and methodological assessment",
      "Originality and contribution evaluation",
      "Reviewer recommendation: accept, revise, or reject",
    ],
  },
  {
    phase: "Step 3",
    week: "2–4 weeks",
    title: "Revision & Resubmission",
    description:
      "If revisions are requested, authors receive detailed reviewer comments and have 30 days to revise and resubmit. The editor evaluates whether revisions adequately address reviewer concerns.",
    activities: [
      "Author receives detailed reviewer feedback",
      "30-day revision window with response document",
      "Editor evaluation of revision quality",
      "Second review round if needed",
    ],
  },
  {
    phase: "Step 4",
    week: "Publication",
    title: "Acceptance, Production & Dissemination",
    description:
      "Accepted manuscripts enter production: copy-editing, layout design, DOI assignment, and indexing. Papers are published online immediately and compiled into the bi-annual print issue.",
    activities: [
      "Copy-editing and proofreading",
      "Layout design and DOI assignment",
      "Online publication and database indexing",
      "Bi-annual print compilation and distribution",
    ],
  },
];

export const ijtaiOutcomes = {
  title: "What IJTAI Delivers for Authors & the Sector",
  items: [
    {
      title: "Indexed Research Output",
      description:
        "Every accepted paper receives a DOI and is indexed in major research databases, giving authors a citable, permanent record of their contribution.",
      icon: "ri-barcode-line",
    },
    {
      title: "Global Visibility",
      description:
        "IJTAI is distributed to textile research institutions, brand sustainability teams, and government agencies worldwide — amplifying Bangladesh T&A research globally.",
      icon: "ri-global-line",
    },
    {
      title: "Sector Benchmark Database",
      description:
        "Over time, IJTAI builds a searchable database of validated factory solutions, technology assessments, and innovation metrics for the entire sector.",
      icon: "ri-database-2-line",
    },
    {
      title: "Career Advancement",
      description:
        "For academics, IJTAI publications count toward tenure and promotion. For factory professionals, they validate expertise and open consulting opportunities.",
      icon: "ri-user-star-line",
    },
    {
      title: "Buyer & Investor Credibility",
      description:
        "Factories with IJTAI-published case studies can present verified innovation credentials to buyers and investors during due diligence.",
      icon: "ri-shield-check-line",
    },
    {
      title: "Knowledge Preservation",
      description:
        "Innovation knowledge that would otherwise remain tacit or undocumented is captured, peer-reviewed, and preserved for future practitioners.",
      icon: "ri-archive-line",
    },
  ],
};

export const ijtaiStats = [
  { value: "12+", label: "Issues Published", sub: "since journal inception" },
  { value: "8–12", label: "Weeks", sub: "average review time" },
  { value: "85+", label: "Papers Published", sub: "from Bangladesh and global authors" },
  { value: "100%", label: "Peer-Reviewed", sub: "every paper double-blind reviewed" },
];